/**
 * src/components/games/launchers/IframeGameActivity.tsx
 *
 * Single-player iframe game activity factory. Each legacy single-player
 * game in PsydeChat is just an iframe pointing at a hosted .pages.dev
 * deployment, wrapped in chrome (header + Exit button) and connected
 * to the framework's lifecycle pings via GameRuntimeProps.onChange.
 *
 * Each launcher module calls `createIframeGame({ id, url, ... })` at
 * module load time. That call (a) defines the Activity component,
 * (b) self-registers a GameManifest into the GamesRegistry, and
 * (c) returns the Activity. GameManager keeps the synchronous
 * `STATIC_COMPONENTS[id]` dispatch by importing each launcher at the
 * top of the file, so the registry is populated at startup. Mirrors
 * the SnakeActivity / CyberChessActivity register-on-import pattern
 * landed in the multiplayer migration.
 *
 * ============================================================================
 *  POSTMESSAGE ENVELOPE CONTRACT — read this if you're a game author
 * --------------------------------------------------------------------------
 *  PsydeChat installs a `window.addEventListener('message', ...)` on the
 *  host page whenever an iframe activity is mounted. The listener
 *  discriminates by `type` + `version` and routes to a database RPC.
 *
 *  ---- score_update (v1) -----------------------------------------------
 *  Snake / Gooch / Kill Trump / Memory Matrix asymmetric scoreboard.
 *
 *    window.parent.postMessage(
 *      {
 *        type:    'score_update',
 *        version: 'v1',
 *        score:   <non-negative integer>,
 *        runId:   '<stable-string>',   // optional but RECOMMENDED
 *        metadata?: {                  // optional — difficulty / map_id etc.
 *          difficulty?: 'easy' | 'medium' | 'hard',
 *          map_id?: string,
 *        },
 *      },
 *      'https://<your-game>.pages.dev'  // game's own origin (targetOrigin)
 *    );
 *
 *  Host: `supabase.rpc('upsert_score', { p_game_id, p_metric, p_score,
 *  p_metadata })` — monotonic. runId is for in-Activity dedupe; DB is
 *  the real safety net.
 *
 *  ---- psydechat:map_complete (v1) --------------------------------------
 *  Per-map fastest completion time. FreeDoom bridge emits this when
 *  the React toolbar fires `psydechat:submit_time`. Memory Matrix
 *  will reuse this same envelope when it adds a time-trial mode.
 *
 *    window.parent.postMessage(
 *      {
 *        type:    'psydechat:map_complete',
 *        version: 'v1',
 *        gameId:  'freedoom',
 *        wad:     'freedoom1',                // optional, for cross-wad dedupe
 *        mapId:   'MAP01',                  // any MAP\d{1,2} string
 *        timeMs:  12345,                   // integer in [1000, 86_400_000]
 *        runId:   'freedoom1-MAP01-1700000000000',  // bridge-generated dedupe key
 *      },
 *      'https://<your-game>.pages.dev'
 *    );
 *
 *  Host: `supabase.rpc('upsert_speedrun', { p_game_id, p_map_id,
 *  p_time_ms })` — monotonic per (user_id, game_id, 'time_ms', map_id).
 *  Gated to launchers that declared `leaderboard: 'time_ms'` so it
 *  cannot fire from a Snake / Gooch / Score-mode launcher by accident.
 *
 *  ============================================================================
 *  ADDING A NEW LAUNCHER
 *  ----
 *  Publish the .pages.dev URL + add a string to ALLOWED_ORIGINS + add
 *  an entry to LAUNCHER_ORIGIN + create the launcher module with
 *  `createIframeGame({ id, url, leaderboard: 'score'|'time_ms', ... })`.
 * ============================================================================
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { registerGame } from '../../../game/framework/GamesRegistry';
import { supabase } from '../../../supabase';
import type {
  GameId,
  GameManifest,
  GameRuntimeProps,
} from '../../../game/framework/types';

/**
 * Browser-enforced origin allowlist. Each string is the canonical
 * `https://host[:port]` form; `event.origin` returned by the browser
 * is always normalized to this same shape, so a `Set.has` literal
 * comparison is sufficient.
 *
 * Adding a new iframe launcher = drop its origin into this set AND
 * add a matching entry to LAUNCHER_ORIGIN. The two policies cross-
 * check each other to prevent cross-game score pollution.
 */
const ALLOWED_ORIGINS = new Set<string>([
  'https://memory-matrix.pages.dev',
  'https://gooch-invaders.pages.dev',
  'https://kill-trump.pages.dev',
  'https://snake-chatterbawx.pages.dev',
]);

/**
 * Per-launcher expected origin. The Activity closure reads this map via
 * `config.id` so each mounted game has its own cross-check. A launcher
 * whose entry is missing here is treated as untrusted (its messages
 * get dropped even if its origin is on the global allowlist — the
 * assumption is that adding a launcher means updating BOTH lists).
 *
 * Type signature: `Record<GameId, string>`. We use `any` for the lookup
 * to keep TS happy even when config.id isn't strictly in the union
 * (e.g., legacy aliases from GameId). The runtime `in` check + null
 * fallback at the call site catches that case.
 */
const LAUNCHER_ORIGIN: Record<string, string> = {
  'memory-matrix': 'https://memory-matrix.pages.dev',
  gooch: 'https://gooch-invaders.pages.dev',
  'kill-trump': 'https://kill-trump.pages.dev',
  'snake-legacy': 'https://snake-chatterbawx.pages.dev',
};

/**
 * The wire format for asymmetric-score envelopes. Game authors should
 * call `buildScoreUpdate(...)` (also exported below) so the shape stays
 * in lockstep with the consumer. Optional fields are omitted from the
 * payload entirely when undefined — easier to version-bump later.
 */
export interface ScoreUpdateV1 {
  readonly type: 'score_update';
  readonly version: 'v1';
  readonly score: number;
  runId?: string;
  metadata?: Readonly<Record<string, unknown>>;
}

/**
 * Per-map fastest-completion envelope produced by the FreeDoom bridge
 * when the React toolbar fires `psydechat:submit_time`. Routed by the
 * host listener to `public.upsert_speedrun(p_game_id, p_map_id, p_time_ms)`
 * — the same RPC the Memory Matrix and future per-map games will use.
 *
 * Envelope name aligns with the legacy `bridge window.PsydeDoom
 * .fireMapComplete()` debug-hook convention (the legacy hook also
 * posts `psydechat:map_complete`); v1 discipline is layered on top
 * via `version` + `runId` so the host listener can discriminate,
 * dedupe, and pick the right RPC.
 *
 * Range invariants mirror the RPC's range check (1000..86_400_000 ms).
 *
 * `runId` is a deterministic `${wad}-${mapId}-${mountedAtMs}` string
 * emitted by the bridge — see FreeDoomLauncher notes. The host uses
 * it for the `firedRunIdsRef.has(runId)` dedupe gate so a chatty
 * double-fire doesn't spam the RPC, but the DB's monotonic upsert
 * remains the real safety net.
 */
export interface SpeedrunUpdateV1 {
  readonly type: 'psydechat:map_complete';
  readonly version: 'v1';
  readonly gameId: string;
  /**
   * FreeDoom-specific: the wad being played (`freedoom1`, `freedoom2`,
   * `freedm`). Reserved for future cross-wad dedupe / analytics; no host
   * consumer reads this field today (the bridge's `runId` already
   * encodes wad as its first segment).
   */
  readonly wad?: string;
  readonly mapId: string;
  readonly timeMs: number;
  readonly runId: string;
}

/**
 * Toolbar API surface — what a launcher can post back to the iframe /
 * bridge via the `emit` closure. Typed union instead of loose strings
 * so a launcher build that emits the wrong shape fails TypeScript
 * before it ships. Extend by adding new variants to the union.
 *
 * Note: `mapId` on `psydechat:submit_time` is currently ignored by the
 * bridge — `mountedMap` is the sole source of truth for which map a
 * submit corresponds to. The field is kept on the wire shape for
 * forward-compat (test harnesses, replay scripts) and for clarity so
 * the toolbar's React-side state can echo what it *would* have
 * submitted without changing the bridge contract.
 */
export type ToolbarEmitEvent =
  | { type: 'psydechat:submit_time'; mapId?: string }
  | { type: 'psydechat:advance_map'; nextMap: string }
  | { type: 'psydechat:reset_timer' };

/**
 * Bridge state surface consumed by the optional `toolbar` slot.
 *
 * Initialized to a `engineReady: false` baseline so a toolbar that
 * reads `state.mountedBundle` before the bridge has had a chance to
 * post its first `psydechat:ready` envelope can render an honest
 * empty-state ("waiting…", "bundle not vendored", etc.) instead of
 * silently displaying the default content.
 *
 * The shape mirrors the payload of the bridge's `psydechat:ready`
 * envelope (see `public/freedoom/launcher-bridge.js`); only fields
 * common across all future same-origin bridges are included here so a
 * Memory-Matrix time-trial or a future custom bridge can use the same
 * slot without forking the type.
 */
export interface ToolbarState {
  engineReady: boolean;
  mountedBundle: string | null;
  currentMap: string | null;
  engineName: string | null;
  engineVersion: string | null;
  /**
   * Set to a non-null code when the bridge fails to report in
   * `psydechat:ready` within the safety timeout (~4s after Activity
   * mount) OR when the bridge posts a `psydechat:error` envelope
   * (engine script failed to load, runtime crash, remount failure).
   * A subsequent `psydechat:ready` envelope always resets this
   * to null so a late-arriving ready recovers the toolbar without a
   * manual reload.
   *
   * String literal (vs `boolean`) lets us extend the error model
   * additively — `'engine load failed'`, `'manifest_corrupt'`,
   * `'csp_blocked'`, `'engine_version_mismatch'`, etc. — without
   * breaking the type for existing launchers.
   */
  engineError: 'bridge unreachable' | 'engine load failed' | null;
  /**
   * The bridge's raw error message (from `psydechat:error`), when one
   * arrived. `null` for the timeout path (no message available) and
   * after a successful `psydechat:ready`. Rendered by the toolbar's
   * red pill so users see the real cause (e.g. `./js-dos.js returned
   * an error event`) instead of a hardcoded guess.
   */
  engineErrorMessage: string | null;
}

const INITIAL_TOOLBAR_STATE: ToolbarState = {
  engineReady: false,
  mountedBundle: null,
  currentMap: null,
  engineName: null,
  engineVersion: null,
  engineError: null,
  engineErrorMessage: null,
};

/**
 * Plain-object shape guard. Arrays are not allowed (they're `typeof
 * 'object'` too but they're not envelopes); null is excluded; class
 * instances are accepted because v1 hasn't seen them yet but if a
 * game author's source uses a class with a .toJSON() it'd round-trip
 * through postMessage as a plain object anyway.
 */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Coerce + range-guard an externally-supplied score. Returns null on
 * anything that can't safely become a positive integer < 1e15.
 *
 * Branches:
 *   typeof !== 'number'  : "42" / "forty-two" / { score: 42 } → null
 *                          (string numbers would coerce in JS but the
 *                          wire format is numbers-only for forward-compat)
 *   !Number.isFinite()    : NaN, Infinity, -Infinity → null
 *   input < 0             : negative scores are fraud (a 16-bit integer
 *                          underflow from a buggy iframe) → null
 *   input > 1e15          : outside the upsert_score RPC's range guard
 *                          (`raise exception 'score out of range'`); bail
 *                          at the listener instead of round-tripping
 *   Math.floor            : drop fractional tails
 */
function sanitizeScore(input: unknown): number | null {
  if (typeof input !== 'number') return null;
  if (!Number.isFinite(input)) return null;
  if (input < 0) return null;
  if (input > 1e15) return null;
  return Math.floor(input);
}

/**
 * Coerce + range-guard a speedrun timeMs. Same shape as sanitizeScore,
 * but tightened to the `upsert_speedrun(p_time_ms between 1000 and
 * 86400000)` RPC invariant so a hostile bridge with a 0-ms or
 * 10-million-ms payload can't even round-trip.
 */
function sanitizeTimeMs(input: unknown): number | null {
  if (typeof input !== 'number') return null;
  if (!Number.isFinite(input)) return null;
  if (!Number.isInteger(input)) return null;
  if (input < 1000 || input > 86_400_000) return null;
  return input;
}

/**
 * Pure formatter MM:SS.cc (centiseconds). Used by both the speedrun
 * listener (toast text) and the FreeDoom toolbar render. Kept tiny
 * and dependency-free so it stays in sync easily across files.
 */
function formatTimeMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const cs = Math.floor((ms % 1000) / 10);
  const sec = totalSec % 60;
  const min = Math.floor(totalSec / 60);
  return (
    String(min).padStart(2, '0') +
    ':' +
    String(sec).padStart(2, '0') +
    '.' +
    String(cs).padStart(2, '0')
  );
}

/**
 * Pure-data utility exported for iframe-side game authors. Game code
 * should call `window.parent.postMessage(buildScoreUpdate({...}), origin)`
 * on game-over without worrying about the wire shape; the listener on
 * the host side enforces the same rules so the contract has only one
 * source of truth.
 *
 * @example
 * // inside an iframe game's game-over handler
 * window.parent.postMessage(
 *   buildScoreUpdate({
 *     score: 1240,
 *     runId: crypto.randomUUID(),
 *     metadata: { difficulty: 'hard' },
 *   }),
 *   'https://memory-matrix.pages.dev',
 * );
 */
export function buildScoreUpdate(input: {
  score: number;
  runId?: string;
  metadata?: Readonly<Record<string, unknown>>;
}): ScoreUpdateV1 {
  const sanitized = sanitizeScore(input.score);
  if (sanitized === null) {
    throw new Error(
      'buildScoreUpdate: score must be a non-negative finite number < 1e15',
    );
  }
  // Single object shape — runId/metadata are optional fields without
  // `readonly` on the ScoreUpdateV1 interface (only the discriminator
  // fields are readonly), so a tiny mutable intermediate is the most
  // TypeScript-friendly construction. With exactOptionalPropertyTypes,
  // optional fields are omitted from the payload entirely when undefined
  // (vs set to `undefined`), which the host listener treats identically.
  const out: ScoreUpdateV1 = {
    type: 'score_update',
    version: 'v1',
    score: sanitized,
  };
  if (input.runId !== undefined) out.runId = input.runId;
  if (input.metadata !== undefined) out.metadata = input.metadata;
  return out;
}

export interface IframeGameConfig {
  id: GameId;
  displayName: string;
  shortDescription: string;
  leaderboard: 'score' | 'time_ms' | null;
  /**
   * Set to true for iframe games whose multiplayer is handled INSIDE the
   * iframe itself — e.g. Quake 3 (QuakeJS) whose client connects to its
   * own WebSocket dedicated server (ioq3ded.js, port 27960). The
   * framework's socket.io NetworkManager is used ONLY for the arcade
   * host-start / peer-request coordination flow; the actual gameplay
   * network lives in the iframe. Defaults to false (single-player
   * activities like FreeDoom / Gooch).
   *
   * When true, the arcade lists the game under "Multiplayer", the host
   * gets a "Host Multiplayer" tile action (broadcasts `change-game` so
   * every peer in the voice channel mounts the same activity — and they
   * all connect to the SAME dedicated server), and peers get the
   * "Request to Play" flow.
   */
  multiplayer?: boolean;
  /**
   * Session shape for `multiplayer: true` iframe games. The framework's
   * `GameActivityManager` was never built, so this is advisory metadata
   * (used by the arcade for labels/max-players) rather than an enforced
   * contract — the game's real networking lives inside the iframe.
   */
  multiplayerShape?: GameManifest['multiplayerShape'];
  /**
   * Whether the game is also launchable as a standalone "Play Solo" tile
   * action. For a self-networked iframe game this is a useful fallback:
   * it mounts the iframe without any socket.io broadcast, so a player
   * can still open the game and connect to the shared server even if the
   * coordination server is unreachable. Defaults to false.
   */
  solo?: boolean;
  /**
   * When `true`, the launcher's iframe is served same-origin (its `url`
   * is a relative path like `/freedoom/index.html` hosted out of
   * `/public/`). The postMessage gates then validate against
   * `window.location.origin` instead of a hard-coded `https://` literal:
   *
   *   - `ALLOWED_ORIGINS` gets `window.location.origin` added at
   *     module-load time so the global cross-launcher check passes.
   *   - `LAUNCHER_ORIGIN[id]` is set to the same value so the per-
   *     launcher cross-check passes.
   *
   * Cross-launcher origin spoofing remains impossible — each launcher
   * still has its own `LAUNCHER_ORIGIN[id]` so a hostile iframe whose
   * origin matches `window.location.origin` would still need to match
   * the *launcher-specific* origin (which equals the same value), but
   * only the launcher corresponding to that id is mounted at a time,
   * so an attacker couldn't spoof a different game's allowlist entry.
   *
   * Use for vendored iframes hosted from `/public/`. Cross-origin
   * iframe launchers (.pages.dev, etc.) leave this off and rely on
   * the existing literal allowlist below.
   */
  sameOrigin?: true;
  url: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  attribution?: GameManifest['attribution'];
  /**
   * Optional toolbar rendered below the iframe. The factory gives the
   * callback an `emit` function to post typed messages into the iframe
   * (validated by the bridge on its end) PLUS a `state` object that
   * mirrors the latest `psydechat:ready` envelope from the bridge —
   * `engineReady`, `mountedBundle`, `currentMap`, `engineError`, etc.
   * (see `ToolbarState`). The `engineError` field is the bridge-
   * unreachable safety-timeout sentinel flipped by the factory's
   * 4-second postMount timer when `psydechat:ready` never arrives.
   *
   * Currently the only consumer is FreeDoomLauncher (the
   * `psydechat:submit_time` / `advance_map` / `reset_timer` envelopes
   * needed to drive `upsert_speedrun`). Other launchers leave this
   * undefined and inherit the default attribution-only footer.
   */
  toolbar?: (api: {
    emit: (event: ToolbarEmitEvent) => void;
    state: ToolbarState;
  }) => ReactNode;
}

export function createIframeGame(config: IframeGameConfig) {
  // Same-origin launchers need to validate postMessages against
  // `window.location.origin` (the iframe inherits the host origin when
  // served from /public/). Register the origin-once here so the
  // module-level ALLOWED_ORIGINS Set + LAUNCHER_ORIGIN map both know
  // about the new launcher. SSR-safe — typeof window guard prevents a
  // crash if createIframeGame is somehow evaluated under Node SSR.
  if (config.sameOrigin === true && typeof window !== 'undefined') {
    const origin = window.location.origin;
    ALLOWED_ORIGINS.add(origin);
    LAUNCHER_ORIGIN[config.id] = origin;
  }
  // Resolve the launcher's expected origin ONCE per Activity instance —
  // the closure reads it directly inside the listener, avoiding a Map
  // lookup on every postMessage. If config.id isn't a recognized
  // launcher, fall through to undefined so the gate becomes a no-op
  // (the global ALLOWED_ORIGINS check still rejects the message
  // unless its origin is on the list).
  const expectedOrigin = LAUNCHER_ORIGIN[config.id];

  const Activity: React.FC<GameRuntimeProps> = ({
    onExit,
    onChange,
    notify,
  }) => {
    // ----------------------------------------------------------------------
    // Lifecycle ping to host: announce this game as the active
    // voice-channel activity on mount, clear the local pill on unmount.
    // Capturing onChange via the closure is intentional — the framework
    // wires a stable callback for the activity's lifetime. Matches the
    // SnakeActivity / CyberChessActivity onChange lifecycle ping.
    // ----------------------------------------------------------------------
    useEffect(() => {
      onChange?.(config.id);
      return () => onChange?.(null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ----------------------------------------------------------------------
    // runId dedupe state. Catches a chatty iframe re-posting the same
    // high score — without dedupe we'd write 200 upsert_score rows for
    // a single run (each a network round-trip). Set is bounded by the
    // number of unique runs a user can play in one session (hundreds
    // at most — not a leak concern). The `?? new Set()` init is
    // defensive in case useRef is called in a render that hasn't yet
    // established the ref (HMR edge cases).
    //
    // Shared by score_update AND psydechat:map_complete so both envelope
    // kinds respect the same per-Activity dedupe window.
    // ----------------------------------------------------------------------
    const firedRunIdsRef = useRef<Set<string> | null>(null);
    if (firedRunIdsRef.current === null) {
      firedRunIdsRef.current = new Set<string>();
    }

    // ----------------------------------------------------------------------
    // iframeRef + emit closure. The optional `toolbar` slot renders
    // below the canvas and gets an `emit` function so it can post
    // typed `ToolbarEmitEvent` messages to the iframe. We don't memo
    // this with useCallback — emit is purely a render-side helper used
    // from event handlers (Submit / Next / Restart clicks), so identity
    // stability across re-renders is unnecessary.
    //
    // targetOrigin is always window.location.origin because the only
    // sandboxed-launcher callers of `toolbar` are same-origin ones
    // (`sameOrigin: true` in their IframeGameConfig). Cross-origin
    // launchers (.pages.dev etc.) don't pass a `toolbar` in v1 — they
    // need browser auto-play / fullscreen allowflags but otherwise carry
    // their own UI inside the iframe.
    // ----------------------------------------------------------------------
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const emit = (event: ToolbarEmitEvent): void => {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      try {
        win.postMessage(event, window.location.origin);
      } catch (e) {
        console.warn('[IframeGameActivity] emit postMessage failed', e);
      }
    };
    // ----------------------------------------------------------------------
    // Same-origin bridges (only `sameOrigin: true` launchers today —
    // FreeDoom out of /public/) wait for a `psydechat:hello` from the
    // parent before doing anything: bootstrapping js-dos, mounting a
    // .jsdos bundle, forwarding heartbeats, etc. Without this the
    // iframe sits forever on the hardcoded HTML splash.
    //
    // targetOrigin memoized so it captures `window.location.origin`
    // once at mount-time (it never changes for the lifetime of a
    // mounted Activity) and the effect deps don't churn.
    // ----------------------------------------------------------------------
    const helloTargetOrigin = useMemo<string | null>(
      () =>
        config.sameOrigin === true ? window.location.origin : null,
      [],
    );
    useEffect(() => {
      if (helloTargetOrigin === null) return;
      const iframe = iframeRef.current;
      if (!iframe) return;
      const target = helloTargetOrigin;
      function sayHello() {
        const win = iframe.contentWindow;
        if (!win) return;
        try {
          win.postMessage({ type: 'psydechat:hello' }, target);
        } catch (e) {
          console.warn(
            '[IframeGameActivity] psydechat:hello postMessage failed',
            e,
          );
        }
      }
      // Fire once immediately in case onload has already fired (StrictMode
      // double-mount, HMR drift, voice-channel activity remount). The
      // bridge's `initPromise` + `if (dosRuntime && mountedBundle) return;`
      // idempotency guards inside its hello handler make a duplicate
      // wake-up call safe (no double-mount of the js-dos runtime).
      sayHello();
      iframe.addEventListener('load', sayHello);
      return () => iframe.removeEventListener('load', sayHello);
    }, [helloTargetOrigin]);
    // ------------------------------------------------------------------
    // Bridge-unreachable safety timeout (~4s after Activity mount).
    // If `psydechat:ready` hasn't arrived by then, flip toolbar state
    // to `engineError='bridge unreachable'` so the toolbar can render
    // the red "Bridge unreachable" pill. The callback form of
    // setToolbarState makes the timer a no-op if ready already arrived
    // (e.g. a slow CDN that recovered at t=3.9s). Cleanup clears the
    // timer on unmount; StrictMode double-mount is safe (first effect
    // tears down before second schedules). SSR-safe — the effect is a
    // no-op for cross-origin launchers (no bridge).
    // ------------------------------------------------------------------
    useEffect(() => {
      if (helloTargetOrigin === null) return;
      const timerId = window.setTimeout(() => {
        setToolbarState((prev) =>
          prev.engineReady
            ? prev
            : {
                ...INITIAL_TOOLBAR_STATE,
                engineReady: true,
                engineError: 'bridge unreachable',
              }
        );
      }, 4000);
      return () => window.clearTimeout(timerId);
    }, [helloTargetOrigin]);
    // ----------------------------------------------------------------------
    // toolbar bridge-state. Mirrored from the bridge's `psydechat:ready`
    // envelope into a `useState` cell so the toolbar slot can render
    // context-aware UI (e.g. FreeDoomToolbar disables Submit/Next/Restart
    // when `mountedBundle === null`).
    // ----------------------------------------------------------------------
    const [toolbarState, setToolbarState] = useState<ToolbarState>(
      INITIAL_TOOLBAR_STATE,
    );
    const toolbarNode = config.toolbar
      ? config.toolbar({ emit, state: toolbarState })
      : null;

    // ----------------------------------------------------------------------
    // postMessage listener with discriminated v1 envelope handling.
    //   - score_update            → supabase.rpc('upsert_score')
    //   - psydechat:map_complete  → supabase.rpc('upsert_speedrun')
    //
    // Re-attached whenever `notify` changes reference. notify is a
    // stable GameRuntimeProps callback in practice (wired by CallLayout
    // once) so this listener is installed exactly once per mount.
    // ----------------------------------------------------------------------
    useEffect(() => {
      function onMessage(event: MessageEvent) {
        // 1. Security gate #1 — global origin allowlist. event.origin
        //    is set by the browser per the iframe document origin (NOT
        //    by payload), so this filter is enforced even if the iframe
        //    code lies about its origin in the message body.
        if (!ALLOWED_ORIGINS.has(event.origin)) return;
        // 2. Security gate #2 — per-launcher expected origin.
        if (expectedOrigin !== undefined && event.origin !== expectedOrigin) return;

        // 3. Envelope validation. Anything that doesn't match a known
        //    shape is dropped silently. We deliberately DO NOT gate on
        //    `data.version === 'v1'` at the listener-top — version is a
        //    per-branch concern (DB-writing branches insist on v1,
        //    UI-only / unread branches accept unversioned for forward-
        //    compat with legacy or experimental envelopes).
        const data = event.data;
        if (!isPlainObject(data)) return;

        // =====================================================================
        //  psydechat:ready branch — bridge status surface
        // ---------------------------------------------------------------------
        //  The bridge posts this on init + each advance_map / reset_timer /
        //  remount. We mirror it into `toolbarState` so the optional
        //  `toolbar` slot can react (e.g. FreeDoomToolbar disables its
        //  Submit/Next/Restart buttons when `mountedBundle === null`).
        // =====================================================================
        if (data.type === 'psydechat:ready') {
          setToolbarState({
            engineReady: true,
            mountedBundle:
              typeof data.mountedBundle === 'string' && data.mountedBundle.length > 0
                ? data.mountedBundle
                : null,
            currentMap:
              typeof data.currentMap === 'string' && data.currentMap.length > 0
                ? data.currentMap
                : null,
            engineName:
              typeof data.engine === 'string' ? data.engine : null,
            engineVersion:
              typeof data.engineVersion === 'string'
                ? data.engineVersion
                : null,
            // Always clear any engineError from a prior safety-timeout
            // fire (e.g., a slow CDN that recovered) — the bridge has
            // now reported, so the only remaining "error" states the
            // toolbar should render are bundle-not-vendored (covered by
            // `mountedBundle === null`) or the engine-error placeholder
            // itself (handled by the timer's no-override check at the
            // callback-form setToolbarState).
            engineError: null,
            engineErrorMessage: null,
          });
          return;
        }

        // =====================================================================
        //  psydechat:error branch — bridge-reported engine failure
        // ---------------------------------------------------------------------
        //  The bridge posts this in several flavors:
        //    stage 'load'    — js-dos.js failed to load (404 / CSP / nosniff
        //                      block → "returned an error event") or the
        //                      .jsdos mount chain failed. FATAL — the red
        //                      pill must show the real cause.
        //    stage 'runtime' — the DOS runtime crashed mid-game. FATAL-ish;
        //                      surface it the same way.
        //    stage 'advance_map' / 'submit_time' / 'remount' — transient
        //                      "bridge not ready" errors the toolbar itself
        //                      can trigger by clicking Next/Submit before
        //                      mount completes. NOT fatal — a red "Engine
        //                      failed to load" pill would be a lie. Drop
        //                      these quietly (the disabled-button gating in
        //                      FreeDoomToolbar already prevents most of
        //                      them; a stray one should not nuke the UI).
        //  A late `psydechat:ready` always recovers (it resets engineError
        //  + message in the ready branch above).
        // =====================================================================
        if (data.type === 'psydechat:error') {
          const stage = typeof data.stage === 'string' ? data.stage : '';
          if (stage !== 'load' && stage !== 'runtime') {
            console.warn(
              `[IframeGameActivity] bridge posted non-fatal error (stage=${stage || 'unknown'}):`,
              data.message,
            );
            return;
          }
          const message =
            typeof data.message === 'string' && data.message.length > 0
              ? data.message
              : null;
          setToolbarState({
            ...INITIAL_TOOLBAR_STATE,
            engineReady: true,
            engineError: 'engine load failed',
            engineErrorMessage: message,
          });
          return;
        }

        // =====================================================================
        //  score_update branch — asymmetric scoreboard
        // ---------------------------------------------------------------------
        //  Only launchers that declared `leaderboard: 'score'` accept score
        //  submissions. FreeDoom (time_ms), CyberChessSolo + DrawTogether
        //  (null) drop the envelope silently because they have no
        //  asymmetric scoreboard story. This is defence-in-depth — the
        //  existing allowlist already constrains which launchers can post,
        //  the `leaderboard` discriminator chooses WHICH OF its two wire
        //  channels accepts the payload.
        // =====================================================================
        if (data.type === 'score_update') {
          if (config.leaderboard !== 'score') return;
          if (data.version !== 'v1') return;
          const sanitized = sanitizeScore(data.score);
          if (sanitized === null) return;

          // runId dedupe.
          const runId =
            typeof data.runId === 'string' && data.runId.length > 0
              ? data.runId
              : null;
          if (runId) {
            const fired = firedRunIdsRef.current;
            if (fired && fired.has(runId)) return;
            if (fired) fired.add(runId);
          }

          // Optional metadata: only forward plain-object payloads so
          // a hostile iframe can't flood the row's metadata jsonb column.
          const metadata = isPlainObject(data.metadata) ? data.metadata : {};

          void supabase
            .rpc('upsert_score', {
              p_game_id: config.id,
              p_metric: 'score',
              p_score: sanitized,
              p_metadata: metadata,
            })
            .then(({ error }) => {
              if (error) {
                notify?.('Score submission failed', {
                  kind: 'error',
                  timeoutMs: 4000,
                });
                console.warn(
                  `[IframeGameActivity] upsert_score failed for ${config.id}:`,
                  error,
                );
                if (runId) firedRunIdsRef.current?.delete(runId);
                return;
              }
              notify?.(
                `Score submitted · ${sanitized.toLocaleString()} pts`,
                { kind: 'info', timeoutMs: 2500 },
              );
            })
            .catch((err: unknown) => {
              console.warn(
                `[IframeGameActivity] upsert_score threw for ${config.id}:`,
                err,
              );
            });
          return;
        }

        // =====================================================================
        //  psydechat:map_complete (v1) — per-map fastest completion time
        // =====================================================================
        if (data.type === 'psydechat:map_complete') {
          // Only launchers that declared `leaderboard: 'time_ms'` accept
          // speedrun submissions. Snake / Gooch / Score-mode launchers
          // drop the envelope silently because they have no map-tracking
          // story.
          if (config.leaderboard !== 'time_ms') return;
          // version gate is per-branch (the listener-top does NOT enforce
          // v1 globally — see comment above).
          if (data.version !== 'v1') return;
          const timeMs = sanitizeTimeMs(data.timeMs);
          if (timeMs === null) return;
          const mapId = typeof data.mapId === 'string' ? data.mapId : '';
          // Reject payloads whose mapId isn't MAPxx-shaped — hostile
          // bridges with broken payloads can't write junk rows.
          if (!/^MAP\d{1,2}$/.test(mapId)) return;
          const runId =
            typeof data.runId === 'string' &&
            data.runId.length > 0 &&
            data.runId.length <= 128
              ? data.runId
              : '';
          if (runId) {
            const fired = firedRunIdsRef.current;
            if (fired && fired.has(runId)) return;
            if (fired) fired.add(runId);
          }
          void supabase
            .rpc('upsert_speedrun', {
              p_game_id: config.id,
              p_map_id: mapId,
              p_time_ms: timeMs,
            })
            .then(({ error }) => {
              if (error) {
                notify?.('Speedrun submission failed', {
                  kind: 'error',
                  timeoutMs: 4000,
                });
                console.warn(
                  `[IframeGameActivity] upsert_speedrun failed for ${config.id} (${mapId}):`,
                  error,
                );
                if (runId) firedRunIdsRef.current?.delete(runId);
                return;
              }
              notify?.(
                `Speedrun submitted · ${mapId} ${formatTimeMs(timeMs)}`,
                { kind: 'info', timeoutMs: 2500 },
              );
            })
            .catch((err: unknown) => {
              console.warn(
                `[IframeGameActivity] upsert_speedrun threw for ${config.id}:`,
                err,
              );
            });
          return;
        }

        // Any other envelope type → drop (we keep the listener silent
        // on noise so a hostile frame can't probe the listener by
        // firing arbitrary `type` values).
        return;
      }
      window.addEventListener('message', onMessage);
      return () => window.removeEventListener('message', onMessage);
    }, [notify]);

    return (
      <div className="relative w-full h-full bg-black text-white flex flex-col overflow-hidden">
        <div className="px-6 py-3 flex items-center justify-between border-b border-white/10 bg-black/60 shrink-0">
          <div className="flex flex-col min-w-0">
            <h2 className="text-xl font-black tracking-tight uppercase truncate">
              {config.displayName}
            </h2>
            <span className="text-[10px] uppercase text-white/40 font-mono tracking-widest truncate">
              {config.shortDescription}
            </span>
          </div>
          <button
            onClick={onExit}
            className="px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-bold uppercase tracking-widest hover:bg-red-500/20 transition-colors flex items-center gap-2 shrink-0"
          >
            <X size={14} />
            Exit
          </button>
        </div>
        <div className="flex-1 bg-black">
          <iframe
            ref={iframeRef}
            src={config.url}
            className="w-full h-full border-none"
            allow="autoplay; fullscreen; gamepad"
            title={config.displayName}
          />
        </div>
        {toolbarNode && (
          // Optional custom toolbar slot (FreeDoom per-map timer + submit,
          // future launchers that want similar controls). Rendered
          // shrink-0 so the iframe's flex-1 stays a true 1fr.
          <div className="shrink-0 border-t border-white/10 bg-black/80 backdrop-blur-sm">
            {toolbarNode}
          </div>
        )}
        {config.attribution && (
          <div className="absolute bottom-2 right-3 text-[9px] text-white/40 font-mono tracking-widest pointer-events-none">
            {config.attribution.text}
          </div>
        )}
      </div>
    );
  };

  // Self-register so importing this module via GameManager's STATIC_COMPONENTS
  // populates the registry before the first render. Idempotent — duplicate
  // registerGame calls replace the prior entry, which is fine under HMR.
  const manifest: GameManifest = {
    id: config.id,
    displayName: config.displayName,
    shortDescription: config.shortDescription,
    multiplayer: config.multiplayer ?? false,
    // Self-networked iframe games (Quake 3) explicitly declare both
    // capabilities: solo mounts the iframe locally, multiplayer drives
    // the arcade host/request flow. Single-player games stay as-is
    // (multiplayer:false ⇒ solo implied, no flag needed).
    solo: config.solo ?? undefined,
    leaderboard: config.leaderboard,
    available: true,
    asset: async () => ({ default: Activity }),
  };
  if (config.multiplayerShape) manifest.multiplayerShape = config.multiplayerShape;
  if (config.icon) manifest.icon = config.icon;
  if (config.attribution) manifest.attribution = config.attribution;
  registerGame(manifest);

  return Activity;
}
